import {
    RoomStateItem,
    RouteHandler,
    RouteHandlerOptions,
    RouteHistoryItem,
    RouteInfo,
    SearchParam,
} from '../../types/state'
import { getSafeNextOrPreviousSurrogate } from '../actions/getNextOrPreviousModel'
import { loadUserIfNeeded } from '../actions/loadUserIfNeeded'
import {
    loadMessageHistory,
    openPm,
    pausePMUnreadCount,
} from '../actions/message'

import { workerArchive } from '../actions/workerActions'
import { RoomView, SettingsType, ViewMode } from '../constants/enums'
import { authenticated } from '../mfcLogic/auth'
import { getLens } from '../mfcLogic/lens'
import {
    addToLoungeRecentModelsSession,
    enabledLounges,
} from '../mfcLogic/lounge'
import { setPinnedRoomSession } from '../mfcLogic/rooms'
import { isLounge } from '../mfcLogic/userProperties/user'
import {
    APPSTATE,
    ARCHIVESTATE,
    HISTORY,
    PMSTATE,
    ROOMSSTATE,
    SEARCHSTATE,
    SETTINGSSTATE,
    TransitionTypes,
    USERSSTATE,
} from '../state'
import {
    getModalState,
    getRoomsState,
    getSlugAndId,
    getUser,
} from '../state/getState'
import { setPMChatOpenState } from '../state/internal/pms'
import { setRoomState, setRoomViewMode } from '../state/internal/rooms'
import { idToSlug, slugToID } from '../state/internal/users'
import {
    closeFloatingRoom,
    minimizeFloatingRoom,
    openFloatingRoom,
} from '../state/modals/state.floater'
import { resetArchiveState } from '../state/state.archives'
import {
    _setRoomOpenState,
    closeRoom,
    isModelViewable,
    pinRoom,
    setRoomIDState,
    setRoomLoungeState,
    setRoomLoungeSurrogateState,
    startAutoPlayInterval,
} from '../state/state.rooms'
import {
    AppRouter,
    getRouteSegments,
    routeConfigs,
} from '../state/state.router'
import {
    resetSearch,
    updateSearchQuery,
    updateSearchView,
} from '../state/state.search'
import { meUser, waitForInitialModelList } from '../state/state.users'
import { initVideo } from '../state/state.video'
import { isMobile as isClientMobile } from './browser'
import { getPageMetadata } from './pageMetadata'
import { motionTransition } from './transitions'

const _initiateNewRoom = (id: number) => {
    const roomStates = getRoomsState()
    // close all other non pinned rooms
    Object.values(roomStates).forEach(({ isPinned, id: existingID }) => {
        if (!existingID || id === existingID || isPinned) {
            return
        }

        closeRoom(existingID)
    })

    // initiate the new room's ID
    setRoomIDState(id)

    // set the room state to open
    _setRoomOpenState(true, id)
}

export const buildRoomRoute = (
    idOrSlug: string | number,
    surrogateIdOrSlug?: string | number,
) => {
    const [slug, id] = getSlugAndId(idOrSlug)
    const [surrogateSlug] = getSlugAndId(surrogateIdOrSlug)

    const roomStates = getRoomsState()

    const isIdLounge = isLounge(id)
    const { [id]: { loungeSurrogateID } = {} as RoomStateItem } = roomStates
    return isIdLounge && (surrogateSlug || loungeSurrogateID)
        ? `/room/${slug}/${surrogateSlug ?? idToSlug[loungeSurrogateID]}`
        : `/room/${slug}`
}

export const closeRoomAndNavBack = async (
    closeRoom = true,
    coords?: { x: number; y: number },
) => {
    // console.log('[closeRoomAndNavBack]', {
    //     basePage: AppRouter.route.baseRoute,
    //     closeRoom,
    //     coords,
    // })
    return routerPush(AppRouter.route.baseRoute, { closeRoom, coords })
}

/** store the previous route in history and actuall call pushState.  shouldn't be used directly */
const route = (
    visibleRoute: string,
    replaceState: boolean,
    initial?: boolean,
) => {
    const PREVIOUS_ROUTE = HISTORY.lastRoute[1]
    const newRoute: RouteHistoryItem = {
        visibleRoute,
        baseRoute: AppRouter.route.baseRoute,
    }
    if (
        PREVIOUS_ROUTE &&
        PREVIOUS_ROUTE.baseRoute === newRoute.baseRoute &&
        PREVIOUS_ROUTE.visibleRoute === newRoute.visibleRoute
    ) {
        // console.log('[route] bailing', { replaceState }, newRoute)
        return
    }

    HISTORY.lastRoute.push(newRoute)
    if (HISTORY.lastRoute.length > 2)
        HISTORY.lastRoute.splice(0, HISTORY.lastRoute.length - 2)
    // console.log(
    //     '[route]',
    //     { visibleRoute, replaceState },
    //     newRoute,
    //     PREVIOUS_ROUTE,
    // )
    if (typeof window !== 'undefined') {
        if (replaceState) {
            if (initial) {
                // Marc: Sometimes browsers will revert this change if it's too
                // early in the page loading process. Apparently putting a
                // timeout is the prescribed way to deal with this...
                window.__MFC_APP_INITIAL_ROUTE_TIMEOUT__ = setTimeout(() => {
                    window.history.replaceState(newRoute, '', visibleRoute)
                    delete window.__MFC_APP_INITIAL_ROUTE_TIMEOUT__
                }, 500)
                return
            }

            if (window.__MFC_APP_INITIAL_ROUTE_TIMEOUT__) {
                clearTimeout(window.__MFC_APP_INITIAL_ROUTE_TIMEOUT__)
            }

            return window.history.replaceState(newRoute, '', visibleRoute)
        } else return window.history.pushState(newRoute, '', visibleRoute)
    }
}

const nonRoomShrinkCheck = (
    visibleRoute: string,
    routeInfo: RouteInfo,
    {
        closeRoom = false,
        coords,
        replaceState = false,
        initial = false,
    }: {
        closeRoom?: boolean
        coords?: { x: number; y: number }
        replaceState?: boolean
        initial?: boolean
    },
) => {
    const { currentRoute, currentSlug } = routeInfo
    // check if current route is room
    const isCurrentRouteRoom = currentRoute === 'room'
    const isCurrentRoutePm = currentRoute === 'message'
    const isCurrentRouteProfile = !!slugToID[currentRoute]
    const isCurrentRouteFloater =
        isCurrentRouteRoom || isCurrentRoutePm || isCurrentRouteProfile
    // only worry about shrinking/closing a room if we're leaving a floater
    if (isCurrentRouteFloater) {
        // get current route's id, if room
        const currentRouteID = currentSlug ? slugToID[currentSlug] : null
        const unauthenticatedMessageRequest =
            isCurrentRoutePm && authenticated() === false

        const roomStates = getRoomsState()
        const modalState = getModalState()
        const { [currentRouteID]: currentRouteState } = roomStates
        const usableVideoID = isLounge(modalState.floating_room.id)
            ? currentRouteState?.loungeSurrogateID
            : modalState.floating_room.id
        if (
            modalState.floating_room.show &&
            !modalState.floating_room.minimized
        ) {
            const { pinnedRooms } = ROOMSSTATE

            // hidden pm video is handled in `minimizeFloatingRoom` as it may need to open a quick reply
            const hiddenProfileVideo =
                modalState.floating_room.roomView === RoomView.Profile &&
                modalState.floating_room.hideVideo

            // check if user has video hidden via settings
            const hideVideoSetting =
                SETTINGSSTATE[SettingsType.ROOM]?.hideVideoModelRoom === 1

            if (
                !closeRoom &&
                isModelViewable(modalState.floating_room.id) && // is still actually broadcasting
                !pinnedRooms.includes(modalState.floating_room.id) && // doesn't already have pinned room
                !hiddenProfileVideo && // ensure we don't open video when coming from profile where video is hidden
                !hideVideoSetting && // don't show preview if user has video hidden
                !unauthenticatedMessageRequest // if a user opens messages as a guest, do not open floater
            ) {
                route(visibleRoute, replaceState)
                const userWantsPreview =
                    SETTINGSSTATE[SettingsType.DEFAULT].enableFloatingPreview

                motionTransition(
                    () => {
                        if (currentRouteState) {
                            _setRoomOpenState(
                                false,
                                modalState.floating_room.id,
                            )
                            if (!userWantsPreview)
                                setRoomState({
                                    id: currentRouteID,
                                    room: {
                                        viewing: false,
                                    },
                                })
                        }
                        // mobile theater, set or pending an orientation restore,
                        // does not follow the room into its mini player (the
                        // page returns to the user's scheme); the preview keeps
                        // it for the room's reopen
                        const reopenViewMode =
                            APPSTATE.app.isMobile &&
                            (modalState.floating_room.viewMode ===
                                ViewMode.Theater ||
                                modalState.floating_room.restoreViewMode ===
                                    ViewMode.Theater)
                                ? ViewMode.Theater
                                : null
                        if (reopenViewMode !== null) {
                            setRoomViewMode(ViewMode.Normal)
                        }
                        minimizeFloatingRoom(
                            modalState.floating_room.id,
                            coords,
                            { reopenViewMode },
                        )
                    },
                    {
                        id: usableVideoID,
                        nav: !initial,
                        skipTransition: !userWantsPreview || !!coords, // only animate if user even wants it
                        transitionSource: TransitionTypes.ROOM,
                        transitionDestination: TransitionTypes.MINI_PLAYER,
                    },
                )
            } else {
                route(visibleRoute, replaceState)
                motionTransition(
                    () => {
                        // this will safely handle if a room is pinned
                        closeFloatingRoom(modalState.floating_room.id)
                        setRoomViewMode(ViewMode.Normal)
                    },
                    {
                        id: usableVideoID,
                        nav: !initial,
                        transitionSource: TransitionTypes.ROOM,
                        transitionDestination:
                            AppRouter.route.lastTransitionSource,
                    },
                )
            }
        }
        return true
    }
    return false
}

const handleGeneralRouting: RouteHandler = async (
    routeInfo: RouteInfo,
    options: RouteHandlerOptions,
) => {
    const { fullRoute, pendingSlug } = routeInfo
    const { closeRoom = false, coords, replaceState, initial } = options

    if (
        nonRoomShrinkCheck(fullRoute, routeInfo, {
            closeRoom,
            coords,
            replaceState,
            initial,
        })
    )
        return

    if (pendingSlug) {
        await loadUserIfNeeded(pendingSlug)
        AppRouter.route.slugID = slugToID[pendingSlug]
    }

    // console.log('[handleGeneralRouting]', fullRoute)
    return motionTransition(() => route(fullRoute, replaceState), {
        nav: !initial,
    })
}

const resolveSearchPath = (
    path: string,
    search: string,
    previousPath: string,
) => {
    if (path === '/search' && !search) {
        if (
            SEARCHSTATE.query &&
            SEARCHSTATE.query.search &&
            !previousPath.startsWith('/search')
        ) {
            const query = '?query=' + SEARCHSTATE.query.search
            const type = '&type=' + SEARCHSTATE.query.type
            //TODO: this should maybe be driven by the query urls and allow the router to flow these back
            AppRouter.route.baseQuery.query = SEARCHSTATE.query.search
            AppRouter.route.baseQuery.type = SEARCHSTATE.query.type
            return path + query + type
        }

        if (SEARCHSTATE.query && SEARCHSTATE.query.view) {
            AppRouter.route.baseQuery.view = SEARCHSTATE.query.view
            return path + '?view=' + SEARCHSTATE.query.view
        }
    }

    return path
}

const handleChatArchivesRouting: RouteHandler = async (
    routeInfo: RouteInfo,
    options: RouteHandlerOptions,
) => {
    const { initial } = options
    const { pendingQuery, fullRoute } = routeInfo
    const { id } = pendingQuery

    // Only trust the cached preload when the in-memory archive state still
    // reflects the general list (no model-specific id). Otherwise an earlier
    // /chat-archives?id=... visit may have left isEmpty=true in state.
    const hasCachedPreload =
        !id &&
        ARCHIVESTATE.state.id === null &&
        AppRouter.route.preloadCache.has('chat-archives')

    resetArchiveState(id ? Number(id) : null, {
        preserveEmpty: hasCachedPreload,
    })

    await waitForInitialModelList()

    if (!hasCachedPreload) workerArchive(id ? { id: Number(id) } : null)

    const { closeRoom = false, coords, replaceState } = options

    if (
        nonRoomShrinkCheck(fullRoute, routeInfo, {
            closeRoom,
            coords,
            replaceState,
            initial,
        })
    )
        return

    // await route(fullRoute, replaceState)
    return motionTransition(() => route(fullRoute, replaceState), {
        nav: !initial,
    })
}

const handleSearchRouting: RouteHandler = async (
    routeInfo: RouteInfo,
    options: RouteHandlerOptions,
) => {
    const { fullRoute, pendingQuery, currentRoute, currentQuery } = routeInfo
    const { view, type, query: search } = pendingQuery as SearchParam
    const { closeRoom = false, coords, replaceState, initial } = options

    const [, { visibleRoute: currentVisibleRoute = '' } = {}] =
        HISTORY.lastRoute
    const isMessagePage = currentRoute === 'message'
    const isRoomPage = currentRoute === 'room'

    if (search) {
        updateSearchQuery({ search, type })

        if (
            currentRoute === 'search' &&
            !isMessagePage &&
            !isRoomPage &&
            !type
        ) {
            return
        }
    }

    if (currentRoute === 'search' && !currentQuery.query && !search) {
        updateSearchView({ view })
    }

    if (
        fullRoute.startsWith('/search') &&
        !isMessagePage &&
        !isRoomPage &&
        !search &&
        currentQuery.query
    ) {
        resetSearch(SEARCHSTATE.query.view)
    }

    const resolvedPath = resolveSearchPath(
        fullRoute,
        search,
        currentVisibleRoute,
    )
    // ensure that the visible route is stored and updated correctly,
    // because we could have restored query values
    AppRouter.route.visibleRoute = resolvedPath

    if (
        nonRoomShrinkCheck(resolvedPath, routeInfo, {
            closeRoom,
            coords,
            replaceState,
            initial,
        })
    )
        return

    // await route(resolvedPath, replaceState)
    return motionTransition(() => route(resolvedPath, replaceState), {
        nav: !initial,
    })
}

const handleProfileRouting: RouteHandler = async (
    routeInfo: RouteInfo,
    options: RouteHandlerOptions,
) => {
    const { fullRoute, pendingRoute, currentRoute, pendingQuery } = routeInfo
    const { slug: ssrSlug } = pendingQuery
    const pendingSlug = ssrSlug || pendingRoute
    const isCurrentRouteRoom = currentRoute === 'room'
    const isCurrentRoutePm = currentRoute === 'message'
    const isCurrentRouteProfile = !!slugToID[currentRoute]
    const isCurrentRouteFloater =
        isCurrentRouteRoom || isCurrentRoutePm || isCurrentRouteProfile
    // get pending route's id
    const pendingRouteID = pendingSlug ? slugToID[pendingSlug] : null
    const isPendingProfileLounge = isLounge(pendingSlug)
    const foundUser = await loadUserIfNeeded(pendingSlug, options.initial)
    if (!foundUser)
        return route('/error?user=' + pendingSlug, options.replaceState)
    AppRouter.route.slugID = pendingRouteID
    AppRouter.route.slug = pendingSlug

    // console.log(
    //     '[handleProfileRouting]',
    //     {
    //         pendingRouteID,
    //         pendingSlug,
    //         pendingQuery,
    //         // routePendingSlug,
    //         // path,
    //         // asPath,
    //         currentRoute,
    //         isCurrentRouteRoom,
    //     },
    //     options,
    // )

    // only try to close if we have a pinned and we're on desktop, as PMs don't have proper
    // passiveMode handling and on desktop PMs can share space with pinned rooms
    if (ROOMSSTATE.pinnedRooms.includes(pendingRouteID) && !isClientMobile()) {
        pinRoom(pendingRouteID, { pinned: false })
    }

    // A profile hides the video of a model who is not live (noProfileVideo),
    // and an offline player would still play her video avatar's sound
    const startVideo = () => {
        const { isBroadcasting, isAway } = getUser(pendingRouteID) || {}
        if (!isPendingProfileLounge && isBroadcasting && !isAway)
            initVideo(pendingRouteID)
    }

    if (options.initial) {
        startVideo()
        // open the room a bit early on direct load so chat/video
        // can get connected earlier and settled quicker
        openFloatingRoom(pendingRouteID, RoomView.Profile)
        return route(fullRoute, options.replaceState)
    }
    // ensure video is started directly by a user action for iOS
    startVideo()

    await motionTransition(
        () => {
            if (ROOMSSTATE.pinnedRooms.includes(pendingRouteID)) {
                pinRoom(pendingRouteID, { pinned: false, skipClose: true })
            }
            openFloatingRoom(pendingRouteID, RoomView.Profile)
        },
        {
            id: pendingRouteID,
            nav: !options.initial,
            skipTransition: isCurrentRouteFloater,
            transitionSource: isCurrentRouteFloater
                ? TransitionTypes.ROOM
                : options.transitionSource,
            transitionDestination: TransitionTypes.ROOM,
        },
    )
    return route(fullRoute, options.replaceState)
}
const handlePmRouting: RouteHandler = async (
    routeInfo: RouteInfo,
    options: RouteHandlerOptions,
) => {
    const { fullRoute, currentRoute, /* currentSlug, */ pendingSlug } =
        routeInfo

    // check if current route is room
    const isCurrentRouteRoom = currentRoute === 'room'
    const isCurrentRoutePm = currentRoute === 'message'
    const isCurrentRouteProfile = !!slugToID[currentRoute]
    const isCurrentRouteFloater =
        isCurrentRouteRoom || isCurrentRoutePm || isCurrentRouteProfile

    const foundUser = await loadUserIfNeeded(pendingSlug, options.initial)
    if (!foundUser)
        return route('/error?user=' + pendingSlug, options.replaceState)

    /*
        Marc: Enabling this would solved the "bug" around the side bar showing
        "Open Rooms" while in PM
        if (isCurrentRouteRoom) {
            closeRoom(currentSlug, { skipCloseIfPinned: true })
        }
    */

    // get pending route's id
    const pendingRouteID = pendingSlug ? slugToID[pendingSlug] : null
    AppRouter.route.slugID = pendingRouteID

    // console.log('[handlePmRouting]', {
    //     pendingRouteID,
    //     path,
    //     asPath,
    //     currentRoute,
    //     isCurrentRouteRoom,
    // })

    // only try to close if we have a pinned and we're on desktop, as PMs don't have proper
    // passiveMode handling and on desktop PMs can share space with pinned rooms
    if (ROOMSSTATE.pinnedRooms.includes(pendingRouteID) && !isClientMobile()) {
        pinRoom(pendingRouteID, { pinned: false })
    }

    if (!pendingRouteID) {
        // update the router state
        const foundBaseRoute = routeConfigs.get('messages')
        AppRouter.route.basePage = 'messages'
        AppRouter.route.baseRoute = '/messages'
        AppRouter.route.baseComponent = foundBaseRoute.component
        AppRouter.route.floatingPage = null
        AppRouter.route.floatingRoute = null
        AppRouter.route.floatingComponent = null
        return route('/messages', options.replaceState)
    }

    if (!PMSTATE.chats[pendingRouteID]?.isHistoryInitiated) {
        loadMessageHistory(pendingRouteID)
    }

    const isAuthenticated = authenticated()

    if (isAuthenticated) {
        await openPm(pendingSlug)
    }

    // A PM hides the video of a model who is not live (openFloatingRoom), and
    // an offline player would still play her video avatar's sound
    const startVideo = () => {
        const { isBroadcasting, isAway } = getUser(pendingRouteID) || {}
        if (isAuthenticated && isBroadcasting && !isAway)
            initVideo(pendingRouteID)
    }

    if (options.initial) {
        // open the room a bit early on direct load so chat/video
        // can get connected earlier and settled quicker
        openFloatingRoom(pendingRouteID, RoomView.PM)
        await waitForInitialModelList()
        route(fullRoute, options.replaceState)
        startVideo()
        pausePMUnreadCount(pendingRouteID)
        setPMChatOpenState(pendingRouteID, true)
        return
    }

    // ensure video is started directly by a user action for iOS
    startVideo()

    await motionTransition(
        () => {
            if (ROOMSSTATE.pinnedRooms.includes(pendingRouteID)) {
                pinRoom(pendingRouteID, { pinned: false, skipClose: true })
            }
            openFloatingRoom(pendingRouteID, RoomView.PM)
            pausePMUnreadCount(pendingRouteID)
            setPMChatOpenState(pendingRouteID, true)
        },
        {
            id: pendingRouteID,
            nav: !options.initial,
            skipTransition: isCurrentRouteFloater,
            transitionSource: isCurrentRouteFloater
                ? TransitionTypes.ROOM
                : options.transitionSource,
            transitionDestination: TransitionTypes.ROOM,
        },
    )

    return route(fullRoute, options.replaceState)
}

const handleRoomRouting: RouteHandler = async (
    routeInfo: RouteInfo,
    options: RouteHandlerOptions,
) => {
    const {
        fullRoute,
        currentRoute,
        pendingSlug,
        currentSlug,
        pendingSurrogateSlug,
    } = routeInfo

    // check if current route is room
    const isCurrentRouteRoom = currentRoute === 'room'
    const isCurrentRoutePm = currentRoute === 'message'
    const isCurrentRouteProfile = !!slugToID[currentRoute]
    const isCurrentRouteFloater =
        isCurrentRouteRoom || isCurrentRoutePm || isCurrentRouteProfile
    const isPendingRoomLounge = isLounge(pendingSlug)

    if (isPendingRoomLounge) {
        await waitForInitialModelList()
    }

    const foundUser = await loadUserIfNeeded(pendingSlug, options.initial)
    if (!foundUser)
        return route('/error?user=' + pendingSlug, options.replaceState)

    const pendingRouteID = pendingSlug ? slugToID[pendingSlug] : null
    AppRouter.route.slugID = pendingRouteID
    // if we're navigating room to room (next/prev, available space, etc)
    if (isCurrentRouteRoom && currentSlug && currentSlug !== pendingSlug) {
        // ensure this sets the viewing state
        setRoomState({
            id: slugToID[currentSlug],
            room: {
                viewing: false,
            },
        })
    }

    const { [pendingRouteID]: pendingRouteState } = getRoomsState()
    // if user is navigating to room (doesn't matter from where) an
    // state does not have the room
    if (!pendingRouteState || !pendingRouteState.isOpen) {
        // initiate new room state
        _initiateNewRoom(pendingRouteID)
    }
    const newPendingRouteState = getRoomsState()[pendingRouteID]

    if (isPendingRoomLounge) {
        // make sure we set the room state to lounge before we do anything else
        setRoomLoungeState(true, pendingRouteID)

        // we have to delay lounge load a tiny bit so ME user is populated
        const loggedIn = await meUser

        const { rewardsPoints, level } = loggedIn
            ? USERSSTATE.users[slugToID['me']]
            : { rewardsPoints: 0, level: 0 }

        const loungesWithAccess = enabledLounges(rewardsPoints, level)
        const hasSpecifiedLoungeAccess = loungesWithAccess?.includes(
            pendingSlug as any,
        )

        if (!hasSpecifiedLoungeAccess) {
            route(fullRoute, options.replaceState)
            motionTransition(() => openFloatingRoom(pendingRouteID), {
                id: pendingRouteID,
                nav: !options.initial,
                skipTransition: isCurrentRouteFloater,
                transitionSource: options.transitionSource,
                transitionDestination: TransitionTypes.ROOM,
            })
            return
        }

        const { loungeSurrogateID } = newPendingRouteState
        if (pendingSurrogateSlug) {
            await loadUserIfNeeded(pendingSurrogateSlug, options.initial)
        }

        const pendingSurrogateID = pendingSurrogateSlug
            ? slugToID[pendingSurrogateSlug]
            : loungeSurrogateID
        const foundSurrogateID =
            getSafeNextOrPreviousSurrogate(pendingSurrogateID)
        AppRouter.route.surrogateID = foundSurrogateID

        // ensure video init is called
        initVideo(foundSurrogateID)
        startAutoPlayInterval(pendingRouteID)
        addToLoungeRecentModelsSession(foundSurrogateID)

        const fullRoomRoute = buildRoomRoute(pendingRouteID, foundSurrogateID)
        setRoomLoungeSurrogateState(foundSurrogateID, pendingRouteID)

        if (isCurrentRouteRoom) {
            // room to lounge
            route(fullRoomRoute, options.replaceState)
            motionTransition(() => openFloatingRoom(pendingRouteID), {
                id: foundSurrogateID,
                nav: !options.initial,
                skipTransition: isCurrentRouteFloater,
                transitionSource: TransitionTypes.ROOM,
                transitionDestination: TransitionTypes.ROOM,
            })
            // activateTransform(foundSurrogateID)
        } else {
            // non-room to lounge
            await motionTransition(() => openFloatingRoom(pendingRouteID), {
                id: foundSurrogateID,
                nav: !options.initial,
                skipTransition: isCurrentRouteFloater,
                transitionSource: options.transitionSource,
                transitionDestination: TransitionTypes.ROOM,
            })
            // activateTransform(foundSurrogateID)
            return route(fullRoomRoute, options.replaceState)
        }
        return
    }
    if (pendingRouteID) {
        const hideVideoSetting =
            SETTINGSSTATE[SettingsType.ROOM]?.hideVideoModelRoom === 1
        if (options.initial) {
            if (!hideVideoSetting) initVideo(pendingRouteID)
            // open the room a bit early on direct load so chat/video
            // can get connected earlier and settled quicker
            openFloatingRoom(pendingRouteID)
            // A directly-loaded room only lives in the worker's in-memory
            // lastViewed, which a reload drops. Persist it so restoreRooms
            // rebuilds it into recently-visited (LAST_VIEWED).
            // open:false -> list it, don't auto-re-pin.
            setPinnedRoomSession(pendingRouteID, { open: false })
            return route(fullRoute, options.replaceState)
        }
        // ensure video is started directly by a user action for iOS
        if (!hideVideoSetting) initVideo(pendingRouteID)
        await motionTransition(
            () => {
                if (ROOMSSTATE.pinnedRooms.includes(pendingRouteID)) {
                    pinRoom(pendingRouteID, { pinned: false, skipClose: true })
                }
                openFloatingRoom(pendingRouteID)
            },
            {
                id: pendingRouteID,
                nav: !options.initial,
                skipTransition: isCurrentRouteFloater,
                transitionSource: isCurrentRouteFloater
                    ? TransitionTypes.ROOM
                    : options.transitionSource,
                transitionDestination: TransitionTypes.ROOM,
            },
        )
        // activateTransform(pendingRouteID)
        return route(fullRoute, options.replaceState)
    }

    // await route(fullRoute, options.replaceState)
    return motionTransition(() => route(fullRoute, options.replaceState), {
        nav: !options.initial,
        transitionDestination: TransitionTypes.ROOM,
    })
}

/**
 * A "smart" wrapper for `route()` calls that is aware of our app.
 *
 * Work towards controlling our routing a bit more by being more proactive about room handling
 * in routing.  Our router should ideally be aware of our overlayed room, as well as video, and
 * be able to leverage view transition. In the meantime, just handle it in the `push` and pretend
 * like we have control
 *
 * @param visibleRoute - the visible component/page (whether top layer or base layer)
 * @param options.baseRoute (optional) - specify the `baseRoute` if `visibleRoute` is a top layer
 * @param options.closeRoom (optional) close the room
 * @param options.initial (optional) initial route from ssr load
 * @param options.transitionSource (optional) source for the transition to start from
 * @param options.replaceState (optional) use replaceState instead of pushState (back/forward nav handling)
 * @param options.coords (optional) coordinates for room preview sticky
 */
export const routerPush = async (
    visibleRoute: string,
    options: RouteHandlerOptions = {},
) => {
    const {
        initial = false,
        baseRoute = visibleRoute,
        replaceState = false,
    } = options

    // we need to do a few things if we have a room open
    // get current visible route
    const [currentRoute, currentSlug, currentSurrogateSlug, currentQuery] =
        getRouteSegments(AppRouter.route.visibleRoute)

    // get pending visible route
    const [
        pendingVisiblePage,
        pendingVisibleSlug,
        pendingVisibleSurrogateSlug,
        pendingVisibleQuery,
        pendingVisibleHash,
        pendingFullVisibleRoute,
    ] = getRouteSegments(visibleRoute)

    if (pendingVisibleQuery) {
        AppRouter.route.visibleQuery = pendingVisibleQuery
    }
    AppRouter.route.hash = pendingVisibleHash

    const foundVisibleRoute = routeConfigs.get(pendingVisiblePage)
    if (!foundVisibleRoute) {
        route('/error', replaceState)
    }

    if (foundVisibleRoute.floater) {
        AppRouter.route.floatingPage = pendingVisiblePage
        AppRouter.route.floatingRoute = pendingFullVisibleRoute
        AppRouter.route.floatingComponent = foundVisibleRoute.component
    }
    AppRouter.route.slug = pendingVisibleSlug
    AppRouter.route.surrogateSlug = pendingVisibleSurrogateSlug

    AppRouter.route.visiblePage = pendingVisiblePage
    AppRouter.route.visibleRoute = visibleRoute

    // get the pending base route
    const [
        pendingBasePage = '',
        ,
        ,
        pendingBaseQuery,
        ,
        pendingFullBaseRoute = '/',
    ] = getRouteSegments(baseRoute)
    const foundBaseRoute = routeConfigs.get(pendingBasePage)
    if (!foundBaseRoute.floater) {
        AppRouter.route.basePage = pendingBasePage
        AppRouter.route.baseRoute = pendingFullBaseRoute
        AppRouter.route.baseComponent = foundBaseRoute.component
        AppRouter.route.baseQuery = pendingBaseQuery
    }

    if (
        foundVisibleRoute.preload &&
        !foundVisibleRoute.skipRouterPreload &&
        !AppRouter.route.preloadCache.has(pendingVisiblePage)
    ) {
        // run fresh preload
        if (foundVisibleRoute.skipAwait) {
            AppRouter.route.preloadCache.set(pendingVisiblePage, {
                promise: foundVisibleRoute.preload(),
                timestamp: Date.now(),
            })
        } else {
            AppRouter.route.preloadCache.set(pendingVisiblePage, {
                promise: await foundVisibleRoute.preload(),
                timestamp: Date.now(),
            })
        }
    }

    // this check won't take into account a baseRoute only change, but im not sure we need to ever do that
    if (
        currentRoute === pendingVisiblePage &&
        currentSlug === pendingVisibleSlug &&
        currentSurrogateSlug === pendingVisibleSurrogateSlug &&
        currentQuery === pendingVisibleQuery &&
        !initial
    ) {
        return
    }

    const isProfile =
        pendingFullVisibleRoute.startsWith('/profile') ||
        (pendingVisiblePage && slugToID[pendingVisiblePage])

    const routeInfo = {
        currentRoute,
        pendingRoute: pendingVisiblePage,
        currentQuery,
        pendingQuery: pendingVisibleQuery,
        currentSlug,
        pendingSlug: pendingVisibleSlug,
        currentSurrogateSlug,
        pendingSurrogateSlug: pendingVisibleSurrogateSlug,
        // route: foundVisibleRoute,
        fullRoute: pendingFullVisibleRoute,
    }

    // console.log(
    //     '[routerPush]',
    //     AppRouter.route,
    //     routeInfo,
    //     {
    //         isProfile,
    //         baseRoute,
    //         visibleRoute,
    //     },
    //     options,
    // )

    // track the page's lens event, declared in pageMetadata
    const { lensEvent, lensData } =
        getPageMetadata(isProfile ? 'profile' : pendingVisiblePage) ?? {}
    if (lensEvent) {
        getLens()?.track(
            lensEvent,
            typeof lensData === 'function' ? lensData(routeInfo) : lensData,
        )
    }

    // Route change start
    AppRouter.events.emit('change', { routeInfo, initial })

    // Routing in process.
    // the route state can be changed here and it won't be guaranteed to be finalized til `completed`
    // should we delay the change event til after these run then?
    switch (pendingVisiblePage) {
        case 'room':
            await handleRoomRouting(routeInfo, options)
            break
        case 'message':
            await handlePmRouting(routeInfo, options)
            break
        case 'search':
            await handleSearchRouting(routeInfo, options)
            break
        case 'chat-archives':
            await handleChatArchivesRouting(routeInfo, options)
            break
        default:
            if (isProfile) {
                await handleProfileRouting(routeInfo, options)
                break
            }
            await handleGeneralRouting(routeInfo, options)
    }

    AppRouter.events.emit('completed', { routeInfo, initial }, false)
}

// console.log('[router running]', AppRouter)
if (typeof window !== 'undefined')
    AppRouter.init(document.location.href.replace(document.location.origin, ''))
